import type { GridPosition } from './types'
import type { TowerEffectSpec } from './effects'
import type { TargetingMode } from './targeting'
import type { DamageType, TargetClass } from '../combat/types/primitives'
import {
  TOWER_DEFINITIONS_V2,
  materializeTowerDefinition
} from '../combat/tower/catalog'

export interface FootprintSize {
  width: number
  height: number
}

export const SELL_REFUND_RATE = 0.6
export const MAX_TOWER_LEVEL = 4

/**
 * Level stats for build/upgrade cost and combat.
 * levels[0].cost = build; levels[n].cost = cost to reach that level from n-1.
 */
export interface TowerLevelDefinition {
  cost: number
  /** Attack range in cell units from footprint center. */
  range: number
  damage: number
  /** Seconds between shots (simulation time). */
  attackInterval: number
  /** Projectile travel speed in cells per second. */
  projectileSpeed: number
  /** 0 = single-target; >0 = splash radius in cells at impact. */
  splashRadius: number
  /** When false, tower places and occupies but does not fire. */
  combatEnabled: boolean
  /** Status effects applied to each hit enemy on projectile impact. */
  effects: TowerEffectSpec[]
  /**
   * Additional simultaneous locks beyond the primary target (same shot cadence).
   * Used by AA LV4 multi-lock — data-driven, no tower-id branch required.
   */
  multiLockTargets?: number
}

export interface TowerDefinition {
  id: string
  name: string
  color: number
  letter: string
  footprint: FootprintSize
  levels: TowerLevelDefinition[]
  /** V2 damage type — defaults via catalog. */
  damageType?: DamageType
  /** V2 target class — defaults via catalog. */
  targetClass?: TargetClass
  visualId?: string
}

/** Logical placed tower. Anchor is top-left; cells grow right and down. */
export interface TowerInstance {
  id: string
  definitionId: string
  anchor: GridPosition
  level: number
  totalInvested: number
  occupiedCells: GridPosition[]
  /** Simulation seconds until the tower may fire again. */
  cooldownRemaining: number
  /** Runtime acquisition preference. Defaults to FIRST on place. */
  targetingMode: TargetingMode
}

/** UI-facing snapshot for the selected tower panel. */
export interface TowerInspectSnapshot {
  id: string
  definitionId: string
  name: string
  letter: string
  color: number
  level: number
  maxLevel: number
  totalInvested: number
  damage: number
  range: number
  attackInterval: number
  projectileSpeed: number
  splashRadius: number
  effects: TowerEffectSpec[]
  targetingMode: TargetingMode
  sellRefund: number
  nextUpgrade: {
    cost: number
    damage: number
    range: number
    attackInterval: number
    splashRadius: number
    effects: TowerEffectSpec[]
  } | null
  canUpgrade: boolean
  canAffordUpgrade: boolean
  /** Match combat telemetry for this tower performance record. */
  damageDealt: number
  kills: number
  shotsFired: number
  hits: number
  splashHits: number
  statusApplications: number
  armorBreakApplications: number
  armorBreakUptime: number
  damageAmplifiedByArmorBreak: number
}

export function footprintCells(anchor: GridPosition, footprint: FootprintSize): GridPosition[] {
  const cells: GridPosition[] = []
  for (let dy = 0; dy < footprint.height; dy += 1) {
    for (let dx = 0; dx < footprint.width; dx += 1) {
      cells.push({ x: anchor.x + dx, y: anchor.y + dy })
    }
  }
  return cells
}

export function footprintCenter(anchor: GridPosition, footprint: FootprintSize): { x: number; y: number } {
  return {
    x: anchor.x + footprint.width / 2,
    y: anchor.y + footprint.height / 2
  }
}

export function levelCost(definition: TowerDefinition, level = 1): number {
  return definition.levels[level - 1]?.cost ?? Number.POSITIVE_INFINITY
}

export function levelRange(definition: TowerDefinition, level = 1): number {
  return definition.levels[level - 1]?.range ?? 0
}

export function levelStats(definition: TowerDefinition, level = 1): TowerLevelDefinition | undefined {
  return definition.levels[level - 1]
}

export function sellRefund(totalInvested: number, rate = SELL_REFUND_RATE): number {
  return Math.floor(totalInvested * rate)
}

export function maxLevel(definition: TowerDefinition): number {
  return definition.levels.length
}

/** Materialized from combat V2 — exact M41 stats. */
export const FIRE_TOWER: TowerDefinition = materializeTowerDefinition(
  TOWER_DEFINITIONS_V2.fire!
)

export const FROST_TOWER: TowerDefinition = materializeTowerDefinition(
  TOWER_DEFINITIONS_V2.frost!
)

export const CANNON_TOWER: TowerDefinition = materializeTowerDefinition(
  TOWER_DEFINITIONS_V2.cannon!
)

export const POISON_TOWER: TowerDefinition = materializeTowerDefinition(
  TOWER_DEFINITIONS_V2.poison!
)

export const AA_TOWER: TowerDefinition = materializeTowerDefinition(TOWER_DEFINITIONS_V2.aa!)

export const MAGE_TOWER: TowerDefinition = materializeTowerDefinition(TOWER_DEFINITIONS_V2.mage!)

export const TOWER_CATALOG: Record<string, TowerDefinition> = {
  fire: FIRE_TOWER,
  frost: FROST_TOWER,
  cannon: CANNON_TOWER,
  poison: POISON_TOWER,
  aa: AA_TOWER,
  mage: MAGE_TOWER
}

export const TOWER_TRAY_ORDER: TowerDefinition[] = [
  FIRE_TOWER,
  FROST_TOWER,
  CANNON_TOWER,
  POISON_TOWER,
  AA_TOWER,
  MAGE_TOWER
]

export function towerDamageType(definition: TowerDefinition): DamageType {
  return definition.damageType ?? 'ENERGY'
}

export function towerTargetClass(definition: TowerDefinition): TargetClass {
  return definition.targetClass ?? 'BOTH'
}
